import { Component, Input, EventEmitter, Output } from '@angular/core';
import { eGENA_ItemListElement } from '../../visionet/ItemListElement';
import { eGENA_ProblemKategorie } from '../../visionet/ProblemKategorieABCDE';

@Component({
	selector: 'ChecklistenItem',
	templateUrl: 'ChecklistenItem.html'
})
export class ChecklistenItemComponent {

	text: string;

	@Input()
	itemList :	eGENA_ItemListElement[] = [];

	/**
	 * (ds)
	 * Im Decorator @Output() wird ein EventEmitter 'eventFuerKlickAufItemLabel' deklariert.
	 * Dadurch kann per eventFuerKlickAufItemLabel.emit(...) ein Event in der
	 * Methode verarbeiteKlickAufLabel weiter unten gefeuert werden. Dieser
	 * Event wird per Listener in ../pages/checklist/checklist.html in Zeile
	 * 48 empfangen:
	 * (eventFuerKlickAufItemLabel)="setZusatzinformationen($event)"
	 * und in der Methode setZusatzinformationen in checklist.ts in Zeile 255 verarbeitet
	 * 
	 * siehe https://ultimatecourses.com/blog/component-events-event-emitter-output-angular-2
	 */
	@Output() 
	eventFuerKlickAufItemLabel = new EventEmitter<{zusatzinformationen:string}>();
 

	@Output() 
	eventFuerLink = new EventEmitter<{checklisteId:string}>();



	ausgeklapptesAccordion : eGENA_ItemListElement | undefined = undefined;

	constructor() {
	}



	/**	=== Visionet(sl) ==========
	 *
	 *	Es wurde auf ein Item-Label geklickt. Jetzt kann folgendes passieren:
	 *	1.	Das Item hat Zusatzinformationen
	 *		Dann wird der Event `eventFuerKlickAufItemLabel` ausgelöst mit der Zusatzinformation `{"zusatzinformationen":item.zusatzinformationen, "index": index}`
	 *	2.	Das Item ist ein Link zu einer Checkliste
	 *		Dann wird zur der Ziel-Checkliste navigiert
	 *	3.	Das Item hat weder Zusatzinformationen noch ist ein ein Link
	 *		In diesem Fall passiert überhaupt nichts
	 *	@param element 
	 */
	verarbeiteKlickAufLabel(element : eGENA_ItemListElement) : void {
		if (element.istAccordionTitel) {
			// KLicke ich auf die gerade ausgeklappte Gruppe? Dann klappe ich wieder ein
			if (this.ausgeklapptesAccordion != undefined && this.ausgeklapptesAccordion.label == element.label) {
				this.ausgeklapptesAccordion = undefined;
			}
			else
				this.ausgeklapptesAccordion = element;
		}
		else {
			if (element.zusatzinformationen != undefined) {
				// Zunächst mal setzen wir in allen Items das Attribut "zusatzinformationenWerdenAngezeigt" zurück
				for (const curItem of this.itemList) {
					curItem.zusatzinformationenWerdenAngezeigt = false;
				}
				element.zusatzinformationenWerdenAngezeigt = true;
				this.eventFuerKlickAufItemLabel.emit({"zusatzinformationen":element.zusatzinformationen});
			} 
			else if (element.linkId != undefined) {
				this.eventFuerLink.emit({"checklisteId":element.linkId.toString()});
			}
		}
	}


	/**	=== Visionet(sl) ==========
	 *
	 *	@param element 
	 */
	hatZusatzinformationen(element : eGENA_ItemListElement) : boolean {
		return element.zusatzinformationen != undefined;
	}


	/**	=== Visionet(sl) ==========
	 *
	 *	@param element 
	 */
	istLinkZuAndererCheckliste(element : eGENA_ItemListElement) : boolean {
		return element.linkId != undefined;
	}


	/**	=== Visionet(sl) ==========
	 *
	 *	@param element 
	 */
	istGeschlossenesAccordion(element : eGENA_ItemListElement) : boolean {
		if (!element.istAccordionTitel)
			return false;
		return this.ausgeklapptesAccordion == undefined || this.ausgeklapptesAccordion.label != element.label;
	}


	/**	=== Visionet(sl) ==========
	 *
	 *	@param element 
	 */
	istGeoeffnetesAccordion(element : eGENA_ItemListElement) : boolean {
		if (!element.istAccordionTitel)
			return false;
		return this.ausgeklapptesAccordion != undefined && this.ausgeklapptesAccordion.label == element.label;
	}


	/**	=== Visionet(sl) ==========
	 *	Regelt die Anzeige-Eigenschaften eines Items. Alle Strings des zurückgelieferten Arrays werden als CSS-Klassen aufgeführt.
	 *
	 *	@param element 
	 */
	getItemCssClass(element : eGENA_ItemListElement) : string[] {
		let cssKlassen : string[] = [];

		if (element.zusatzinformationen != undefined || element.linkId != undefined || element.istAccordionTitel)
			// Wenn so ein Item entweder ein Link ist oder Zusatzinformationen hat, dann ist das ganze Label klickbar und das sollte man dann auch sehen
			cssKlassen.push("klickbar");

		if (element.checked)
			cssKlassen.push("abgehakt");

		if (element.zusatzinformationenWerdenAngezeigt)
			cssKlassen.push("zusatzinformationenSichtbar");

		if (element.istAccordionTitel)
			cssKlassen.push("ist-accordion-titel");
		else {
			cssKlassen.push("ist-item");
			if (element.gehörtZuItemGruppe != undefined)
				cssKlassen.push("gehoert-zu-item-gruppe");
		}

		let pks : string = eGENA_ProblemKategorie.problemkategorienAsString(element);
		if (pks != undefined && pks.length > 0) {
			cssKlassen.push("hat-problemkategorie");
			cssKlassen.push("problemkategorie-" + pks);
		}

		return cssKlassen;
	}


	/**	=== Visionet(sl) ==========
	 *	Regelt die Anzeige-Eigenschaften des umgebenden Elements eines Items. Alle Strings des zurückgelieferten Arrays werden als CSS-Klassen aufgeführt.
	 *
	 *	@param element 
	 */
	getItemWrapCssClass(element : eGENA_ItemListElement) : string[] {
		let cssKlassen : string[] = [];

		if (element.gehörtZuItemGruppe != undefined)
		cssKlassen.push("gehoert-zu-item-gruppe");

		return cssKlassen;
	}


	showCheckbox(element : eGENA_ItemListElement) {
		return element.checkbox;
	}


	/**	=== Visionet(sl) ==========
	 *	Soll das betroffene Element gerade angezeigt werden?
	 *
	 *	Ja, wenn	- Es sich um ein normales Item handelt, das zu keiner Item-Gruppe gehört
	 *				- Es sich um ein normales Item handelt, das zu der Item-Gruppe gehört, die gerade ausgeklappt ist
	 *				- Es sich um ein Accordin handelt, also die Einleitung einer Item-Gruppe
	 *
	 *	@param element 
	 */
	shouldShowElement(element : eGENA_ItemListElement) : boolean {
		// Items, die zu einer Accordin-Gruppe gehören, werden zunächst mal nicht angezeigt.
		if (element.gehörtZuItemGruppe != undefined) {
			if (this.ausgeklapptesAccordion != undefined && this.ausgeklapptesAccordion.label == element.gehörtZuItemGruppe)
				return true;
			return false;
		}

		return true;
		
	}

}



// WEBPACK FOOTER //
// ./src/components/ChecklistenItem/ChecklistenItem.ts